import { useAuth } from '@/context/AuthContext';
import useSignOut from '@/context/useSignOut';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLeftSidebar } from '@/hooks/useLeftSidebar';
import { useSettings } from '@/hooks/useSettings';
import useAnalytics from '@/lib/analytics/useAnalytics';
import {
  ChevronDown,
  Chrome,
  LaptopIcon,
  LogOut,
  MoonIcon,
  SettingsIcon,
  SunIcon,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useRouter } from 'next/router';
import React from 'react';
import { Button } from '../Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { Icon } from '../ui/Icon';

const AccountMenu = () => {
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const { user, userPlanDetails } = useAuth();
  const { signOut } = useSignOut();
  const { setReturnPage } = useSettings();

  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const isMobile = useIsMobile();
  const { setShowLeftSidebar } = useLeftSidebar();

  const { setTheme } = useTheme();
  const { track } = useAnalytics();

  const _chromeStoreUrl =
    'https://chromewebstore.google.com/detail/unriddle-ai-generated-sum/iinmigjlcpeckfihbbfajpkiilfmakff?authuser=0';

  const displayName = user?.workspaceName || user?.displayName || 'Library';
  const truncatedName =
    displayName.length > 15
      ? `${displayName.substring(0, 15)}...`
      : displayName;
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          isLoading={isSigningOut}
          variant="ghost"
          className="text-sm w-fit font-semibold gap-0.5 h-7"
          loadingClassName="flex flex-col items-start"
          size="xs"
        >
          {truncatedName}
          <Icon as={ChevronDown} size="sm" strokeWidth={2.5} />
        </Button>
      </DropdownMenuTrigger>
      {user && (
        <DropdownMenuContent align="start" className="min-w-[244px]">
          <DropdownMenuLabel className="flex flex-col py-1 px-2">
            <div>
              <div className="flex flex-row w-full gap-2 items-center">
                <p className="text-sm font-semibold">
                  {user.displayName && user.displayName.length > 24
                    ? `${user.displayName.substring(0, 24)}...`
                    : user.displayName}
                </p>
                <div className="text-xs font-semibold text-badgeBlue bg-badgeLightBlue py-0.5 px-1 rounded-sm">
                  {userPlanDetails?.planName}
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setReturnPage(router.asPath);
              router.push('/settings/account');
              if (isMobile) setShowLeftSidebar(false);
            }}
          >
            <Icon size="sm" as={SettingsIcon} />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Icon size="sm" as={SunIcon} />
              Theme
            </DropdownMenuSubTrigger>

            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-36">
                <DropdownMenuItem
                  onSelect={() => {
                    setTheme('light');
                    track('theme_changed', { theme: 'light' });
                  }}
                >
                  <Icon size="sm" as={SunIcon} /> Light
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setTheme('dark');
                    track('theme_changed', { theme: 'dark' });
                  }}
                >
                  <Icon size="sm" as={MoonIcon} /> Dark
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setTheme('system');
                    track('theme_changed', { theme: 'system' });
                  }}
                >
                  <Icon size="sm" as={LaptopIcon} /> System
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem
            onSelect={() => {
              window.open(_chromeStoreUrl, '_blank');
            }}
          >
            <Icon size="sm" as={Chrome} />
            Chrome extension
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={async () => {
              setOpen(false);
              setIsSigningOut(true);
              await signOut().catch(() => {
                setIsSigningOut(false);
              });
            }}
          >
            <Icon size="sm" as={LogOut} />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
};

export default AccountMenu;
